<template>
  <ResponsiveModal
    :title="$t('components.asset_groups.create.title')"
    :description="$t('components.asset_groups.create.description')"
    :confirmLabel="$t('components.buttons.save')"
    :withClose="false"
    variant="create"
    alignButtons="center"
    @confirm="
      handleAddAssetGroup(item ? (item.value ? item.value.id : item.id) : null)
    "
    :closeOnConfirm="false"
    :disableCancel="isAdding"
    :disableConfirm="isAdding || !(isDirty || !isEmpty(names))"
    @update:open="handleUpdateOpen"
    :open="open"
  >
    <slot :disabled="!canCreateAssetGroups" />
    <template #content>
      <div class="flex flex-col items-center p-4">
        <span v-if="maxSmall">
          {{ $t("components.asset_groups.create.description") }}
        </span>
      </div>
      <div class="flex flex-col grow border-t overflow-y-auto">
        <div class="flex flex-col grow py-4 px-5 md:px-3 bg-secondary">
          <GroupsInput
            as="textarea"
            :placeholder="
              $t('components.asset_groups.create.form.name.placeholder')
            "
            v-model="names"
            variant="asset_group"
            @update:isDirty="isDirty = $event"
          />
        </div>
      </div>
    </template>
  </ResponsiveModal>
</template>

<script setup>
import { ref, computed, onMounted, inject, nextTick, watch } from "vue";
import { storeToRefs } from "pinia";
import route from "ziggy-js";
import { usePage, router } from "@inertiajs/vue3";

import { ResponsiveModal } from "@/SharedComponents/ResponsiveModal";
import { GroupsInput } from "@/SharedComponents/Inputs";

import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useAssetGroups } from "@/Composables/useAssetGroups";
import { useFilters } from "@/Composables/useFilters";
import { useSites } from "@/Composables/useSites";
import { isEmpty } from "lodash";

// Breakpoints
const { maxSmall } = useBreakpoints();

// Filters: Global
const globalFilterStore = useFilters("global");
const { filterValues: globalFilterValues } = storeToRefs(globalFilterStore);

// Buildings
const { isLoading: isLoadingBuildings, data: buildingsData } = useSites();

// Permissions
const { hasPermission, permissions } = inject("permissions");

// Refs
const names = ref([]);
const open = ref(false);
const isAdding = ref(false);
const isDirty = ref(false);

// Computed props
const url = computed(() => usePage().url);
const buildingId = computed(() => globalFilterValues.value.tenant?.[0] || "");

const buildings = computed(() => {
  if (isLoadingBuildings.value) {
    return [];
  }

  return buildingsData.value.filter((building) =>
    hasPermission(permissions.createAsset, building.id)
  );
});

const canCreateAssetGroups = computed(() => {
  if (!buildingId.value) {
    return !isEmpty(buildings.value);
  }

  return hasPermission(permissions.createAsset, buildingId.value);
});

// Asset groups
const { addAssetGroup } = useAssetGroups();

// Emitter
const emitter = inject("emitter");

// Define Props
const props = defineProps({
  item: { type: Object, required: false },
});

const handleUpdateOpen = (value) => {
  open.value = value;
  names.value = [];

  if (!value) {
    isAdding.value = value;
  }

  if (isEmpty(props.item)) {
    if (!value && url.value.includes("/admin/asset-groups/new")) {
      router.get(
        route("asset_groups.index"),
        {},
        { preserveState: true, replace: true }
      );
    } else {
      router.get(
        route("asset_groups.new"),
        {},
        { preserveState: true, replace: true }
      );
    }
  }
};

const handleAddAssetGroup = (parentGroupId) => {
  isAdding.value = true;

  addAssetGroup(names.value, parentGroupId, () => {
    emitter.emit("reload-asset-groups");

    nextTick(() => {
      handleUpdateOpen(false);
    });
  });
};

onMounted(() => {
  if (url.value == "/admin/asset-groups/new") {
    open.value = true;
  }
});

watch(url, () => {
  if (url.value == "/admin/asset-groups/new") {
    open.value = true;
  }
});
</script>
